/* eslint-disable no-useless-escape */
import {
  FieldValidationArguments,
  FieldValidationFunctionSignature,
  FieldValidationResult,
  FormFieldValueObject,
  GooglePlace,
  MultiSchoolSelectionState,
} from 'types';
import { getDataForZip, validatePhone } from 'app-requests/triadmsRequests';
import { DEFAULT_SELECT_VALUE, FIELD_NAMES, VALIDATION_TYPES } from 'consts';
import { getGooglePlaceAddressParts } from 'components/base/inputs/googleAddressLookup/getGooglePlaceAddressParts';
import { isValidEmail, isValidZip } from './formValuesUtils';
import { LogWarning } from './logging';

const { PRIMARY_PHONE_TYPE, SECONDARY_PHONE, SECONDARY_PHONE_TYPE } =
  FIELD_NAMES;

/**
 * @summary This is used to validate zip code by calling our backend API
 */
export function validateZip(
  args: FieldValidationArguments<string>
): FieldValidationResult | Promise<FieldValidationResult> {
  const { value, name, errorMessage } = args;
  if (!value) return Promise.resolve({});

  if (!isValidZip(value)) return { [name]: 'Invalid Zip Code' };

  return getDataForZip({ zip: value }).then(
    ({ isValid }: { isValid: boolean }) => {
      if (!isValid) {
        return {
          [name]: errorMessage || 'Invalid Zip Code',
        };
      }
      return {};
    }
  );
}

/**
 * @summary This is used to validate Phone Numbers by calling our backend API
 */
async function validatePhoneNumber(
  args: FieldValidationArguments<string>
): Promise<FieldValidationResult> {
  const { value, name, formValues } = args;
  if (!value) return Promise.resolve({});

  // needs to be 10 digest
  if (value.replace(/-/g, '').length !== 10)
    return Promise.resolve({ [name]: 'Invalid Phone Number' });

  function hasValue(fieldName: FIELD_NAMES): boolean {
    return Boolean(
      formValues &&
        formValues[fieldName] &&
        (formValues[fieldName] as FormFieldValueObject<string>).value !==
          DEFAULT_SELECT_VALUE.value
    );
  }

  // always needs primary phone type
  if (!hasValue(PRIMARY_PHONE_TYPE)) {
    return Promise.resolve({
      [FIELD_NAMES.PRIMARY_PHONE_TYPE]: 'Please select a phone type.',
    });
  }

  const primaryNumberResults = await validatePhone(value);

  if (!primaryNumberResults.isValid) {
    return { [name]: 'Invalid Phone Number' };
  }

  if (
    hasValue(SECONDARY_PHONE) &&
    typeof formValues?.[SECONDARY_PHONE] === 'string'
  ) {
    const secondaryNumber = formValues?.[SECONDARY_PHONE] as string;
    if (secondaryNumber.replace(/-/g, '').length !== 10) {
      return Promise.resolve({ [SECONDARY_PHONE]: 'Invalid Phone Number' });
    }

    if (!hasValue(SECONDARY_PHONE_TYPE)) {
      return Promise.resolve({
        [FIELD_NAMES.SECONDARY_PHONE_TYPE]: 'Please select a phone type.',
      });
    }

    const secondaryNumberResults = await validatePhone(secondaryNumber);

    if (!secondaryNumberResults.isValid) {
      return Promise.resolve({ [SECONDARY_PHONE]: 'Invalid Phone Number' });
    }
  }

  return {};
}

/**
 * @summary This is used to validate Emails
 */
export function validateEmail(
  args: FieldValidationArguments<string>
): FieldValidationResult | Promise<FieldValidationResult> {
  const { value, name, errorMessage } = args;
  if (!isValidEmail(value)) {
    return Promise.resolve({
      [name]: errorMessage || 'Please enter a correct Email Address Format.',
    });
  }

  return Promise.resolve({});
}

/**
 * @summary This is used to validate Street Address
 * @see https://triadms.atlassian.net/browse/T1-5352
 */
export function validateStreet(
  args: FieldValidationArguments<string>
): FieldValidationResult | Promise<FieldValidationResult> {
  const { value, name, errorMessage } = args;

  const errorObj = { [name]: errorMessage || 'Invalid Street' };
  const hasNumbersRegex = /\d+/g;
  const hasCharRegex = /[a-zA-Z]+/g;
  const hasProperSpacing = /^\s*\S+(?:\s+\S+){1}/;

  if (!hasNumbersRegex.test(value)) {
    return Promise.resolve(errorObj);
  }

  if (!hasCharRegex.test(value)) {
    return Promise.resolve(errorObj);
  }

  if (!hasProperSpacing.test(value)) {
    return Promise.resolve(errorObj);
  }

  return Promise.resolve({});
}

/**
 * @summary This is used to validate MultiSchoolSelectionState
 *          for having at least one school accepted or skipped
 */
export function validateSchoolSkipOrSubmit(
  args: FieldValidationArguments<MultiSchoolSelectionState>
): FieldValidationResult {
  const { value, name, errorMessage } = args;
  const errorObj = {
    [name]:
      errorMessage || 'Please accept the terms and conditions for each school.',
  };

  const hasUnAcknowledgedSchools = Object.values(value).some(
    (schoolSelectionState) =>
      !schoolSelectionState.isAccepted && !schoolSelectionState.isSkipped
  );

  if (hasUnAcknowledgedSchools) {
    return errorObj;
  }

  return {};
}

export function validateGoogleAddressLookup(
  args: FieldValidationArguments<GooglePlace>
): FieldValidationResult {
  const { value, name } = args;

  const { streetNumber, streetRoute, city, state, zip } =
    getGooglePlaceAddressParts(value);

  let errorMessage;

  if (!streetNumber) {
    errorMessage = 'Please enter a street number.';
  }

  if (!streetRoute) {
    errorMessage = 'Please enter a street name.';
  }

  if (!city) {
    errorMessage = 'Please enter a city.';
  }

  if (!state) {
    errorMessage = 'Please enter a state.';
  }

  if (!zip) {
    errorMessage = 'Please enter a zip code.';
  }

  if (errorMessage) {
    LogWarning(`Google Address Lookup Validation Error: ${errorMessage}`);
    return { [name]: errorMessage };
  }

  return {};
}

const FIELD_VALIDATION_MAP: Record<
  VALIDATION_TYPES,
  FieldValidationFunctionSignature<never>
> = {
  [VALIDATION_TYPES.ZIP]: validateZip,
  [VALIDATION_TYPES.EMAIL]: validateEmail,
  [VALIDATION_TYPES.PHONE]: validatePhoneNumber,
  [VALIDATION_TYPES.STREET]: validateStreet,
  [VALIDATION_TYPES.SCHOOL_SKIP_OR_SUBMIT]: validateSchoolSkipOrSubmit,
  [VALIDATION_TYPES.GOOGLE_ADDRESS_LOOKUP]: validateGoogleAddressLookup,
};

export default FIELD_VALIDATION_MAP;
